import { useState, useCallback, useRef } from 'react';
import moment from 'moment';
import { Modal, notification } from 'tea-component';
import { getDecisionPointsList, updateDecisionPoints, deleteDecisionPoints } from '@sg/services/index';
import { filterFontSize } from '@sg/utils/commonPageMethod';

interface UseDecisionPointsProps {
  selectedMonth: string | null;
  formatMessage: any;
  maxDataTime: string;
}

export const useDecisionPoints = ({ selectedMonth, formatMessage, maxDataTime }: UseDecisionPointsProps) => {
  const DPTRef = useRef<any>(null);
  const tablePointRef = useRef<any>(null);

  // State
  const [decisionPointsData, setDecisionPointsData] = useState<any[]>([]); // View mode: current_edit, Edit mode: last_edit
  const [currentEditData, setCurrentEditData] = useState<any[]>([]); // Store current_edit from API
  const [lastEditData, setLastEditData] = useState<any[]>([]); // Store last_edit from API
  const [originalDecisionPointsData, setOriginalDecisionPointsData] = useState<any[]>([]); // Always last_edit
  const [decisionPointsLoading, setDecisionPointsLoading] = useState(false);
  const [isDPTEditing, setIsDPTEditing] = useState(false);
  const [pendingDeletes, setPendingDeletes] = useState<any[]>([]);

  // Fetch Decision Points
  const fetchDecisionPoints = useCallback(async (month?: moment.Moment) => {
    setDecisionPointsLoading(true);
    try {
      // 如果传入了月份参数，使用传入的月份；否则使用当前的 selectedMonth
      // selectedMonth is a string (YYYY-MM) or null, so we need to convert it to moment
      const targetMonth = month || (selectedMonth ? moment(selectedMonth) : (maxDataTime ? moment(maxDataTime) : moment()));
      const startDate = targetMonth.startOf('month').format('YYYY-MM-DD');
      const endDate = targetMonth.endOf('month').format('YYYY-MM-DD');
      const res: any = await getDecisionPointsList({
        start_date: startDate,
        end_date: endDate
      });

      const newResData = filterFontSize(res?.data);
      const { current_edit, last_edit } = newResData;
      // const { current_edit, last_edit } = res?.data;

      // Store both current_edit and last_edit separately
      const currentEdit = current_edit || [];
      const lastEdit = last_edit || [];

      setCurrentEditData(currentEdit);
      setLastEditData(lastEdit);

      // View mode: show current_edit (what user sees by default)
      // Edit mode will use last_edit (switched when entering edit mode)
      setDecisionPointsData(currentEdit);

      // Original data is always last_edit (for comparison when saving)
      setOriginalDecisionPointsData(JSON.parse(JSON.stringify(lastEdit)));
    } catch (error) {
      notification.error({
        description: 'Error fetching decision points',
      });
      setDecisionPointsData([]);
      setOriginalDecisionPointsData([]);
    } finally {
      setDecisionPointsLoading(false);
    }
  }, [selectedMonth, maxDataTime]);

  // Save Decision Points
  const handleSaveDecisionPoints = useCallback(async (data: any, clearAutoSave: () => void) => {
    try {
      setDecisionPointsLoading(true);

      // Handle deletes first
      if (pendingDeletes.length > 0) {
        const recordsToDelete = pendingDeletes.filter((record) => {
          const isNewRow = record.id && String(record.id).startsWith('new_');
          return !isNewRow;
        });

        if (recordsToDelete.length > 0) {
          const deletePromises = recordsToDelete.map((record) =>
            deleteDecisionPoints({ id: record.id })
          );
          await Promise.all(deletePromises);
        }

        setPendingDeletes([]);
      }

      // Transform data: replace date with selectedMonth's first day
      // Use selectedMonth if available, otherwise use maxDataTime, fallback to current month
      const targetMonth = selectedMonth ? moment(selectedMonth) : (maxDataTime ? moment(maxDataTime) : moment());
      const firstDayOfMonth = targetMonth.startOf('month').format('YYYY-MM-DD');

      const transformedData = data.map((item: any) => ({
        ...item,
        date: firstDayOfMonth, // Replace date with selectedMonth's first day
      }));

      await updateDecisionPoints({ items: transformedData });
      clearAutoSave();

      // Exit edit mode first
      setIsDPTEditing(false);

      // Fetch updated data - now isDPTEditing is false, so it will show current_edit
      // Use setTimeout to ensure state update has been processed
      setTimeout(() => {
        fetchDecisionPoints();
      }, 0);
    } catch (err) {
      console.error('Save error:', err);
      notification.error({
        description: 'Failed to save decision points. Please try again.',
      });
      setDecisionPointsLoading(false);
    }
  }, [pendingDeletes, fetchDecisionPoints, selectedMonth, maxDataTime]);

  // Add new row
  const handleAddNewRow = useCallback(() => {
    const currentMonth = selectedMonth ? moment(selectedMonth) : moment();
    const monthName = currentMonth.format('MMMM');

    const newRow = {
      id: `new_${Date.now()}`,
      date: currentMonth.startOf('month').format('YYYY-MM-DD'),
      game_id: 0,
      name: '',
      status: '',
      urgency: '',
      expected_time: monthName,
      issue: '',
      bos: '',
    };

    if (DPTRef.current) {
      DPTRef.current.setEditData((prevData: any[]) => [newRow, ...prevData]);
      DPTRef.current.setTableData((prevData: any[]) => [newRow, ...prevData]);
    }
  }, [selectedMonth]);

  // Delete row
  const onDeleteRow = useCallback(async (record: any, isManualUpdateRef: any, setDecisionPointsEditData: any) => {
    const confirmed = await Modal.confirm({
      className: 'dbs-delete-confirm-modal',
      message: formatMessage({ id: 'dbs_delete_project_confirm' }),
      okText: formatMessage({ id: 'dbs_confirm' }),
      cancelText: formatMessage({ id: 'dbs_cancel' }),
      size: 'auto'
    });

    if (confirmed) {
      isManualUpdateRef.current = true;

      setPendingDeletes((prev) => [...prev, record]);

      setDecisionPointsEditData((prev: any[]) => {
        const filtered = prev.filter((item: any) => item.id !== record.id);
        console.log('🗑️ [DELETE] Removed from decisionPointsEditData:', record.id, 'New length:', filtered.length);
        return filtered;
      });

      if (DPTRef.current) {
        DPTRef.current.setEditData((prevData: any[]) => prevData.filter((item: any) => item.id !== record.id));
        DPTRef.current.setTableData((prevData: any[]) => prevData.filter((item: any) => item.id !== record.id));
      }
    }
  }, [formatMessage]);

  // Editing state change handler
  const handleEditingChange = useCallback((isEditing: boolean) => {
    setIsDPTEditing(isEditing);

    // When entering edit mode, switch to last_edit data
    // When exiting edit mode, switch back to current_edit data
    if (isEditing) {
      // Entering edit mode: use last_edit
      setDecisionPointsData(lastEditData);
    } else {
      // Exiting edit mode: use current_edit
      setDecisionPointsData(currentEditData);
    }
  }, [currentEditData, lastEditData]);

  return {
    // Refs
    DPTRef,
    tablePointRef,

    // State
    decisionPointsData,
    setDecisionPointsData,
    currentEditData,
    lastEditData,
    originalDecisionPointsData,
    decisionPointsLoading,
    isDPTEditing,
    pendingDeletes,
    setPendingDeletes,

    // Functions
    fetchDecisionPoints,
    handleSaveDecisionPoints,
    handleAddNewRow,
    onDeleteRow,
    handleEditingChange,
  };
};
